import LoginBanner from './LoginBanner';
import LoginFooter from './LoginFooter';
import LoginForm from './LoginForm';
import { useLogin } from '../../context/LoginContext';
import { useModal } from '../../context/ModalContext';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import * as credentialStore from '../../services/credentialStore';
import matrixAuthService from '../../services/matrixAuthService';
import { isSupersededInvalidation } from '../../utils/sessionInvalidation';
import './LoginPage.css';
import { useEffect } from 'react';

const LoginPage = () => {
  const { setIsAuthenticated, isAuthenticated } = useLogin();
  const { showInfo, showError } = useModal();
  const { t } = useI18n();
  const navigate = useNavigate();
  // Raw presence of a stored session (boolean); used to redirect an already-
  // authenticated visitor away from the login page.
  const savedCreds = credentialStore.hasCredentials();

  useEffect(() => {
    // Check for session invalidation (race condition case - token refresh failed on startup)
    let invalidatedData = null;
    let logoutReason = null;
    try {
      invalidatedData = sessionStorage.getItem('matrix:session-invalidated');
      logoutReason = sessionStorage.getItem('logout_reason');
    } catch (e) {
      // sessionStorage unavailable (private browsing, storage disabled)
    }

    if (invalidatedData && !isAuthenticated) {
      // NED-4397: the flag body is JSON (both SET sites JSON.stringify it), but
      // parse defensively — a malformed/legacy value must fall through to
      // "shown" (fail-safe), never silently suppress the modal.
      let parsed = null;
      try {
        parsed = JSON.parse(invalidatedData);
      } catch {
        parsed = null;
      }

      // Always consume the flag on this mount.
      try {
        sessionStorage.removeItem('matrix:session-invalidated');
      } catch (e) {
        // Ignore
      }

      // NED-4397: only pop the "Session Expired" modal if this invalidation is
      // NOT superseded by a newer login (its loginNonce is not older than the
      // current session's). A superseded flag is a stale tail landing during a
      // transient /login mount of an already re-authenticated user — clearing it
      // (above) is enough; showing the modal would orphan it over the app.
      if (!isSupersededInvalidation(parsed, matrixAuthService.getLoginNonce())) {
        showInfo(
          t('LoginPage.sessionExpiredTitle'),
          t('LoginPage.sessionExpiredMessage'),
        );
      }
    }

    // Check for inactivity logout
    if (logoutReason === 'inactivity' && !isAuthenticated) {
      showInfo(
        t('LoginPage.inactivityLogoutTitle'),
        null,
      );
      // Clear the flag so the modal doesn't reappear on refresh
      try {
        sessionStorage.removeItem('logout_reason');
      } catch (e) {
        // Ignore
      }
    }

    // Check for SSO error
    let ssoErrorMsg = null;
    try {
      ssoErrorMsg = sessionStorage.getItem('sso:error');
    } catch {
      // sessionStorage unavailable
    }

    if (ssoErrorMsg && !isAuthenticated) {
      showError(
        t('LoginPage.ssoErrorTitle'),
        ssoErrorMsg,
      );
      try {
        sessionStorage.removeItem('sso:error');
      } catch {
        // Ignore
      }
    }

    // Check for SSSS detection failure (server error during encryption status check)
    let ssssFailedData = null;
    try {
      ssssFailedData = sessionStorage.getItem('ssss_detection_failed');
    } catch {
      // sessionStorage unavailable
    }

    if (ssssFailedData && !isAuthenticated) {
      showError(
        t('ssssDetectionFailed.title'),
        t('ssssDetectionFailed.message'),
        () => {
          // Remove flag only when user dismisses the modal, not immediately.
          // The ModalProvider may remount during logout (resetting modal state),
          // and the flag must survive so the effect can re-show the error.
          try { sessionStorage.removeItem('ssss_detection_failed'); } catch { /* ignore */ }
        },
      );
    }
  }, [showInfo, showError, isAuthenticated, t]);

  useEffect(() => {
    if (savedCreds && !isAuthenticated) {
      setIsAuthenticated(true);
    }
    if (isAuthenticated) {
      navigate('/conversations', { replace: true });
    }
  }, [isAuthenticated, setIsAuthenticated, navigate, savedCreds]);

  return (
    <div className="login-page">
      <div className="login-banner-container">
        <LoginBanner />
      </div>
      <div className="login-form-container">
        <LoginForm />
        <LoginFooter />
      </div>
    </div>
  );
};

export default LoginPage;